/**
 * useImageUpload Hook
 * 
 * Reusable hook for handling image uploads to S3 via presigned URLs
 * Can be used for profile photos, business logos, or any other image uploads
 * 
 * Usage:
 * const { uploadImage, isUploading, error, imageUrl } = useImageUpload();
 * 
 * // In your component
 * <input type="file" onChange={(e) => uploadImage(e.target.files?.[0])} />
 */

import { useState } from 'react';
import {
    getProfileImageUploadUrl,
    uploadImageToS3,
    updateProfileImage,
    getProfileImages
} from '../services/profileService';

interface UploadResult {
    success: boolean;
    imageUrl?: string;
    imageKey?: string;
    error?: string;
}

interface UseImageUploadOptions {
    maxSizeMB?: number;
    allowedTypes?: string[];
    folderType?: string; // Added folderType option
    imageType?: string; // Added imageType option
    onSuccess?: (result: UploadResult) => void;
    onError?: (error: string) => void;
}

export const useImageUpload = (options: UseImageUploadOptions = {}) => {
    const {
        maxSizeMB = 5,
        allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'],
        folderType = 'profile-images', // Default to profile-images
        imageType = 'profile', // Default to profile
        onSuccess,
        onError
    } = options;

    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [progress, setProgress] = useState(0);

    /**
     * Validate file before upload
     */
    const validateFile = (file: File): { valid: boolean; error?: string } => {
        // Check file type
        if (!allowedTypes.includes(file.type)) {
            const allowedExtensions = allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ');
            return {
                valid: false,
                error: `Please upload a valid image file (${allowedExtensions})`
            };
        }

        // Check file size
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            return {
                valid: false,
                error: `File size should not exceed ${maxSizeMB}MB`
            };
        }

        return { valid: true };
    };

    /**
     * Upload image to S3 and update profile
     */
    const uploadImage = async (file: File | null | undefined): Promise<UploadResult> => {
        if (!file) {
            return { success: false, error: 'No file provided' };
        }

        // Reset state
        setError(null);
        setProgress(0);

        // Validate file
        const validation = validateFile(file);
        if (!validation.valid) {
            const errorMessage = validation.error || 'Invalid file';
            setError(errorMessage);
            onError?.(errorMessage);
            return { success: false, error: errorMessage };
        }

        setIsUploading(true);

        try {
            // Step 1: Get presigned URL from backend
            console.log('Step 1: Getting presigned upload URL...');
            setProgress(25);
            const response = await getProfileImageUploadUrl(file.type, folderType);
            console.log('Raw response from backend:', response);

            // Extract data from response (API returns { data: { upload_url, image_key }, success: true })
            const responseData = (response as any).data || response;
            const upload_url = responseData.upload_url;
            const image_key = responseData.image_key;

            console.log('✅ Received presigned URL:', upload_url);
            console.log('✅ Received image key:', image_key);

            // Step 2: Upload image to S3
            console.log('Step 2: Uploading image to S3...');
            console.log('S3 URL:', upload_url);
            setProgress(50);
            await uploadImageToS3(upload_url, file);
            console.log('✅ Image uploaded to S3');

            // Step 3: Update profile with image key
            console.log('Step 3: Updating profile with image key...');
            setProgress(75);
            const updateResponse = await updateProfileImage(image_key, imageType);
            console.log('Raw update response:', updateResponse);

            // Step 4: Fetch fresh image URLs to ensure we have the correct display URL
            console.log('Step 4: Fetching fresh profile images...');
            const imagesResponse = await getProfileImages();
            console.log('Fresh images response:', imagesResponse);

            // Step 5: Update state with the new image URL
            setProgress(100);
            let finalImageUrl: string | null = null;

            if (imagesResponse.success && imagesResponse.data) {
                if (imageType === 'business') {
                    finalImageUrl = imagesResponse.data.business_logo_url || null;
                } else {
                    finalImageUrl = imagesResponse.data.profile_image_url || null;
                }
            }

            // Fallback if fetch failed or returned empty
            if (!finalImageUrl) {
                // Extract data from update response
                const updateData = (updateResponse as any).data || updateResponse;

                if (updateData.image_url) {
                    finalImageUrl = updateData.image_url;
                } else if (updateData.profile_image_url) {
                    finalImageUrl = updateData.profile_image_url;
                } else if (responseData.image_url) {
                    finalImageUrl = responseData.image_url;
                } else {
                    finalImageUrl = URL.createObjectURL(file);
                }
            }

            setImageUrl(finalImageUrl);

            const successResult: UploadResult = {
                success: true,
                imageUrl: finalImageUrl || undefined,
                imageKey: image_key
            };

            onSuccess?.(successResult);
            return successResult;

        } catch (err) {
            console.error('Image upload error:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
            setError(errorMessage);
            onError?.(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setIsUploading(false);
        }
    };

    /**
     * Clear error state
     */
    const clearError = () => {
        setError(null);
    };

    /**
     * Reset all state
     */
    const reset = () => {
        setError(null);
        setImageUrl(null);
        setProgress(0);
    };

    return {
        uploadImage,
        isUploading,
        error,
        imageUrl,
        progress,
        clearError,
        reset
    };
};
